import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  Footer as BaseFooter,
  contentSchema as baseSchema,
} from '@vibesites/theme-preferred/components/Footer';

/**
 * Dietz Property Group override of the parent Footer.
 *
 * Visual-only changes layered on top of the parent via the style-wrapper
 * pattern:
 *  1. Darken the bottom band for WCAG AA contrast (see note below).
 *  2. Enlarge the "Professionally Managed by" company logo so the managing
 *     brand reads more prominently than the secondary Powered-By / Design-By
 *     attribution marks.
 *
 * Bottom-band contrast: the parent Footer paints the bottom band with
 * `color-mix(in srgb, var(--vibe-color-secondary) 70%, black)`. When the
 * brand secondary color is on the lighter end (as it is for Dietz), the
 * resulting band is a medium/light gray that does not meet WCAG AA contrast
 * against the `text-white/70` legal nav, compliance icons, and attribution
 * row that sit on top of it.
 *
 * Per the override-decision table this is a CSS-only tweak, so we use the
 * style-wrapper pattern: render the parent unchanged and ship an extra
 * `<style>` block that overrides `.footer-bottom`. CSS specificity is equal
 * (single class selector), so document order wins — our rule, declared after
 * the parent's inline `<style>`, takes effect.
 *
 * The new mix is `color-mix(in srgb, var(--vibe-color-secondary) 15%, black)`:
 * mostly black with a faint tint of the brand color, so the band still feels
 * connected to the primary band above without compromising contrast against
 * the 70%-opacity white text. This pushes the visible-text-vs-background
 * ratio comfortably past WCAG AA (4.5:1) on any reasonable secondary color.
 *
 * The schema re-attach is required — `contentSchema` is bound to function
 * identity, and the registry resolves it from `Footer.contentSchema`. Re-
 * exporting the parent's schema symbol (rather than re-declaring) ensures
 * future parent edits to tokens/slot groups flow through to this wrapper.
 */
export function Footer() {
  return (
    <>
      <BaseFooter />
      <style>{`
        /* Darken bottom band for WCAG AA contrast vs. text-white/70 content. */
        .footer-bottom {
          background: color-mix(in srgb, var(--vibe-color-secondary) 15%, black);
        }

        /*
         * Enlarge the "Professionally Managed by" company logo.
         *
         * The parent renders this image with Tailwind utilities (h-4 w-auto
         * max-w-[100px]) which makes it visually equivalent to the smaller
         * Powered By / Design By attribution marks. For Dietz the managing
         * company is the primary brand affiliation in the footer and should
         * read more prominently than the secondary attributions.
         *
         * The managed-by group is the first child of .footer-attribution-row;
         * targeting it via descendant <img> avoids needing to override the
         * markup. Specificity (0,2,1) beats Tailwind's single-class utilities
         * (0,1,0) so this wins without !important.
         */
        .footer-attribution-row > div:first-child img {
          height: 2rem;
          max-width: 180px;
        }
      `}</style>
    </>
  );
}

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = baseSchema;

export default Footer;
